import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isEmpty, map } from 'lodash';
import PropTypes from 'prop-types';
import {
  filterOutOptionalExtrasFromFerryReservation,
  formatDate,
  fullFerrySelfServiceEnabled,
  getReservationsByProductType,
  isComplexBookingFromQT,
  isRepeatableStop,
  processPassengerType,
  trackError,
} from 'utils/helpers';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import { removeAllFerryAndAssociatedVehicles } from 'redux/actions/reservation/delete';
import { setFerryGrouping } from 'redux/actions/ui/sealink';
import {
  getCurrentBooking,
  getOptionalExtraReservations,
  getOptionalExtras,
  getPrices,
} from 'redux/selectors/current_booking';
import { isDateInPast } from 'tenants/sealink/booking_details/constants';
import { FerryView, FerryViewNoData } from './ferry_view';
import {
  getSegment,
  getSegmentDetail,
  getSegmentDetailWithDate,
  sortByDateAndTime,
  sortByStartTime,
} from './helpers';
import useGetVehicles from './use_get_vehicles';

const FerryBooking = ({ onlyShowInfo }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  // Get settings of sealink from CMS
  const { service_routes: serviceRoutes, segments } = useSelector(
    (state) => state.sealink || {}
  );
  // Get settings from qt
  const {
    productTypes: qtProductTypes = [],
    passengerTypes: qtPassengerTypes = [],
  } = useSelector((state) => state.types || {});

  const allRoutes = useSelector((state) => state.routes);
  const booking = useSelector(getCurrentBooking);
  const optionalExtras = useSelector(getOptionalExtras);
  const optionalExtraReservations = useSelector(getOptionalExtraReservations);

  // We only need ferry and transfer here.
  let ferryAndTransfers = getReservationsByProductType(
    'ferry,transfer',
    booking
  );
  ferryAndTransfers = filterOutOptionalExtrasFromFerryReservation({
    reservations: ferryAndTransfers,
  });
  const hasFerryReservation = !isEmpty(ferryAndTransfers);

  const ferries = [];

  const repeatableStop = isRepeatableStop({
    routeId: ferryAndTransfers?.[0]?.route_id,
    allRoutes,
  });

  const { isActive, isSimpleBooking, isFreightBooking } =
    useCurrentBookingState();

  const [isRemovingReservation, setIsRemovingReservation] = useState(false);

  const isReverseJourney = (departures, returns) => {
    // One-way journey
    if (returns.length === 0) {
      return false;
    }

    // One-way reverse journey
    if (departures.length === 0) {
      return true;
    }

    const departureFirstDate = departures[0].first_travel_date._value;
    const returnFirstDate = returns[0].first_travel_date._value;

    // Return journey in the same day
    if (departureFirstDate === returnFirstDate) {
      return (
        departures[0].start_time.split('T')[1] >
        returns[0].start_time.split('T')[1]
      );
    }

    // Return journey in different days
    return departureFirstDate > returnFirstDate;
  };

  const showCancelBtn = useMemo(() => {
    /*
      Do not display the cancel reservation link when any of the following conditions are met:
      - Draft booking feature is not enabled
      - Booking contains a freight vehicle
      - Booking contains non-ferry reservations (accommodation, experience)
      - Booking has only 1 ferry reservation
      - Is not active booking
      - Ferry reservation has `time_restricted: true`
     */
    if (!fullFerrySelfServiceEnabled()) {
      return {
        departure: false,
        return: false,
      };
    }

    // Freight bookings cannot be cancelled online
    if (isFreightBooking) {
      return { departure: false, return: false };
    }

    const ferryReservations = getReservationsByProductType('ferry', booking);
    const otherReservations = getReservationsByProductType(
      'accommodation,experience',
      booking
    );

    // Don't show cancel if booking contains non-ferry reservations
    if (otherReservations.length > 0) {
      return {
        departure: false,
        return: false,
      };
    }

    if (ferryReservations.length <= 1) {
      return {
        departure: false,
        return: false,
      };
    }

    if (booking.state !== 'active') {
      return {
        departure: false,
        return: false,
      };
    }

    return {
      departure: !ferryReservations[0].time_restricted,
      return: ferryReservations[1]
        ? !ferryReservations[1].time_restricted
        : false,
    };
  }, [booking, isFreightBooking]);

  // Determine if cancel unavailable notification should show per reservation.
  // This is true when all conditions for cancel are met EXCEPT the reservation
  // is time_restricted (i.e. departure is too soon for online cancellation).
  const showCancelUnavailableNotification = useMemo(() => {
    if (!fullFerrySelfServiceEnabled()) {
      return { departure: false, return: false };
    }

    // Freight bookings cannot be cancelled online — hide links with no
    // replacement per-leg notification.
    if (isFreightBooking) {
      return { departure: false, return: false };
    }

    const ferryReservations = getReservationsByProductType('ferry', booking);
    const otherReservations = getReservationsByProductType(
      'accommodation,experience',
      booking
    );

    if (otherReservations.length > 0) {
      return { departure: false, return: false };
    }

    if (ferryReservations.length <= 1) {
      return { departure: false, return: false };
    }

    if (booking.state !== 'active') {
      return { departure: false, return: false };
    }

    // Only show the per-leg "too close to departure" notification when the
    // reservation is time-restricted but has NOT already departed.  Already-
    // travelled legs are covered by the booking-level notification in
    // BookingDetailsOverview (with the correct "already departed" message),
    // so showing a per-leg notification here would duplicate it with the
    // wrong wording.
    return {
      departure:
        Boolean(ferryReservations[0].time_restricted) &&
        !isDateInPast(ferryReservations[0].first_travel_date?._value),
      return: ferryReservations[1]
        ? Boolean(ferryReservations[1].time_restricted) &&
          !isDateInPast(ferryReservations[1].first_travel_date?._value)
        : false,
    };
  }, [booking, isFreightBooking]);

  const optionalExtraReservationIds = optionalExtraReservations.map(
    (optionalExtra) => optionalExtra.id
  );
  const buildFerrySimple = () => {
    let departures = [];
    let returns = [];

    const allDepartureSegments = serviceRoutes
      .map((route) => route.forward_journey.segments)
      .flat();

    if (repeatableStop) {
      // When it comes to Groote, route id is same for departure and return
      // Hence we cannot get any useful information
      const [first, second] = ferryAndTransfers;
      if (first) {
        departures = [first];
      }
      if (second) {
        returns = [second];
      }
    } else {
      ferryAndTransfers.forEach((reservation) => {
        const segment = getSegment(reservation.route_id, segments);

        // Ignore ferryAndTransfers that don’t match a segment
        if (!segment) return;

        // Determine if a reservation is departure or return
        if (allDepartureSegments.includes(segment.id)) {
          departures.push(reservation);
        } else {
          returns.push(reservation);
        }
      });
    }

    if (isReverseJourney(departures, returns)) {
      const departuresCopy = departures;
      departures = [...returns];
      returns = [...departuresCopy];
    }

    const sortedDepartures = departures.sort(sortByStartTime);
    // departures might be [] when editing a booking - https://bitbucket.org/team-sealink/stg-website-platform/pull-requests/1843#comment-302192452
    const departureDate = sortedDepartures[0]
      ? formatDate(sortedDepartures[0]?.first_travel_date._value)
      : '';
    const result = {
      departureFerry: {
        date: departureDate,
        showCancelBtn: showCancelBtn.departure,
        showCancelUnavailableNotification:
          showCancelUnavailableNotification.departure,
        reservationIds: [
          ...optionalExtraReservationIds, // Remove all optional extras whenever cancelling a trip
          ...sortedDepartures.map((reservation) => reservation.id),
        ],
        products: sortedDepartures.map((reservation) =>
          getSegmentDetail(reservation, qtProductTypes)
        ),
      },
    };

    if (returns.length) {
      const sortedReturns = returns.sort(sortByStartTime);

      result.returnFerry = {
        date: formatDate(sortedReturns[0].first_travel_date._value),
        showCancelBtn: showCancelBtn.return,
        showCancelUnavailableNotification:
          showCancelUnavailableNotification.return,
        reservationIds: [
          ...optionalExtraReservationIds, // Remove all optional extras whenever cancelling a trip
          ...sortedReturns.map((reservation) => reservation.id),
        ],
        products: sortedReturns.map((reservation) =>
          getSegmentDetail(reservation, qtProductTypes)
        ),
      };
    }

    return result;
  };

  const buildFerryComplex = () => {
    let services = [];

    ferryAndTransfers.forEach((reservation) => {
      const segment = getSegment(reservation.route_id, segments);
      // Ignore ferryAndTransfers that don’t match a segment
      if (!segment) return;
      services.push(reservation);
    });
    services = services.sort(sortByDateAndTime);
    services = services.map((reservation) =>
      getSegmentDetailWithDate(reservation, qtProductTypes)
    );

    return { services };
  };

  const buildFerry = isComplexBookingFromQT(booking)
    ? buildFerryComplex
    : buildFerrySimple;

  const {
    passengerPrices,
    passengerAdjustments,
    ferrySubtotalPrice,
    commissionPrice,
    commissionPercentage,
  } = useSelector(getPrices);

  // Build passenger data
  const passengerTypes = hasFerryReservation
    ? processPassengerType(
        ferryAndTransfers[0].passenger_splits,
        booking.passengers_attributes,
        qtPassengerTypes
      )
    : [];
  const passengers = map(passengerTypes, (value, key) => ({
    name: key,
    price: passengerPrices[key],
    count: value.count,
    adjustments: passengerAdjustments[key] || [],
  }));

  const processedVehicles = useGetVehicles(booking);

  const customerFacingAdjustments = useMemo(() => {
    const groups = {};
    ferryAndTransfers.forEach((reservation) => {
      (reservation.adjustments_attributes || [])
        .filter((adj) => adj.display_to_customer)
        .forEach((adj) => {
          const key = adj.adjustment_category_id;
          if (!groups[key]) {
            groups[key] = {
              category_id: key,
              display_name: adj.display_name,
              total: 0,
            };
          }
          groups[key].total += adj.gross_in_cents;
        });
    });
    return Object.values(groups);
  }, [ferryAndTransfers]);

  const result = {
    ...buildFerry(),
    price: ferrySubtotalPrice,
    commissionPrice,
    commissionPercentage,
    passengers,
    optionalExtras,
    customerFacingAdjustments,
    vehicles: processedVehicles,
  };

  ferries.push(result);

  const handleRemoveFerryBooking = async () => {
    if (isEmpty(booking) || isEmpty(booking?.reservations_attributes)) {
      return;
    }
    setIsRemovingReservation(true);
    dispatch(removeAllFerryAndAssociatedVehicles())
      .then(() => {
        dispatch(setFerryGrouping([]));
        setIsRemovingReservation(false);
        history.push('/bookings');
      })
      .catch((error) => {
        trackError(error);
        setIsRemovingReservation(false);
      });
  };

  const populateActions = () => {
    const actionList = {};

    if (!isActive) {
      actionList.removeFerry = handleRemoveFerryBooking;
      actionList.editFerry = (url) => history.push(url);
    } else if (isSimpleBooking) {
      actionList.editFerry = () =>
        history.push(`/bookings/${booking.id}/ferry/services/departure`);
    }

    return actionList;
  };

  return (
    <>
      {ferries.length ? (
        <>
          {/* eslint-disable react/no-array-index-key */}
          {ferries.map((ferry, index) => (
            <FerryView
              onlyShowInfo={onlyShowInfo}
              ferry={ferry}
              actions={populateActions()}
              isDeletingReservation={isRemovingReservation}
              key={index}
            />
          ))}
        </>
      ) : (
        <FerryViewNoData />
      )}
    </>
  );
};

FerryBooking.propTypes = {
  onlyShowInfo: PropTypes.bool,
};

export default FerryBooking;
